import cloneDeep from 'lodash/cloneDeep';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { CSSTransitionGroup } from 'react-transition-group';

import {
  ADD_TEAMMATE,
  CREATE_ACCOUNT,
  DOWNLOAD_RECORDER,
  FIRST_VIDEO_RECORDING,
  FIRST_VIDEO_VIEWED,
  SHARE_VIDEO,
} from 'constants/checklist';
import {
  GET_STARTED_CHECKLIST_EXPANDED,
  GET_STARTED_CHECKLIST_MINIMIZED,
} from 'constants/events';
import * as analytics from 'utilities/analytics';
import { useGetStartedChecklistState } from 'utilities/localStorage/onboarding';

import { useGetCheckListStatus } from 'hooks/getStartedChecklist';

import CollapsedChecklist from './CollapsedChecklist';
import DownloadCTA from './item-cta/download';
import InviteCTA from './item-cta/invite';
import RecordCTA from './item-cta/record';
import ShareCTA from './item-cta/share';
import ViewCTA from './item-cta/view';
import MiniChecklist from './MiniChecklist';
import OpenChecklist from './OpenChecklist';
import { FadeCallout } from '../fade-callout';

import styles from './index.module.less';

export const MAX_ACCOUNT_AGE = 7; // do not show this checklist for accounts older than this many days
const TASK_COMPLETION_ANIMATION_DURATION = 4000;
// Keep task IDs in the same order they are to initially appear on the checklist
const TASK_IDS = [
  CREATE_ACCOUNT,
  DOWNLOAD_RECORDER,
  FIRST_VIDEO_RECORDING,
  SHARE_VIDEO,
  FIRST_VIDEO_VIEWED,
  ADD_TEAMMATE,
];

const collapsedChecklistTransitions = {
  enter: styles.collapsedChecklistEnter,
  leave: styles.collapsedChecklistLeave,
};

export default function GetStartedChecklist({ displayMode }) {
  const [animatedTaskId, setAnimatedTaskId] = useState(null);
  const checklistStatus = useGetCheckListStatus();
  const [state, dispatch] = useReducer(
    reducer,
    null,
    initState(checklistStatus)
  );
  const statusCache = useRef(checklistStatus);
  const animationTimeoutRef = useRef();
  const openTimeoutRef = useRef();

  const [checklistIsMinimized, minimizeChecklist] =
    useGetStartedChecklistState();

  const { tasks, currentTask, openTask } = state;
  const incompleteTasks = tasks.filter(t => !t.isComplete);
  const showOpen = !checklistIsMinimized;

  const openChecklist = () => {
    analytics.track(GET_STARTED_CHECKLIST_EXPANDED);
    minimizeChecklist(false);
  };

  const closeChecklist = () => {
    analytics.track(GET_STARTED_CHECKLIST_MINIMIZED);
    minimizeChecklist(true);
  };

  const openTaskDisclosure = id => {
    dispatch({
      type: 'OPEN_DISCLOSURE',
      id,
    });
  };

  const completeTask = useCallback(
    taskId => {
      dispatch({
        type: 'COMPLETE_TASK',
        task: taskId,
      });

      clearTimeout(animationTimeoutRef.current);
      clearTimeout(openTimeoutRef.current);

      setAnimatedTaskId(taskId);
      animationTimeoutRef.current = setTimeout(() => {
        setAnimatedTaskId(null);
      }, TASK_COMPLETION_ANIMATION_DURATION);

      if (openTask === taskId) {
        const nextTask = tasks.find(t => !t.isComplete && t.id !== taskId);

        if (nextTask) {
          openTimeoutRef.current = setTimeout(() => {
            openTaskDisclosure(nextTask.id);
          }, 450);
        }
      }
    },
    [openTask, tasks]
  );

  useEffect(() => {
    TASK_IDS.forEach(id => {
      const prev = statusCache.current[id];
      const next = checklistStatus[id];

      if (next !== prev) {
        statusCache.current[id] = next;
        completeTask(id);
      }
    });
  }, [checklistStatus, completeTask]);

  const isCompact = displayMode === 'COMPACT';
  const isCollapsed = displayMode === 'COLLAPSED';

  return (
    <div className="absolute">
      <FadeCallout
        className="absolute bottom:0"
        visible={!isCollapsed}
        width="expanded"
      >
        <MiniChecklist
          isCompact={isCompact}
          animatedTaskId={animatedTaskId}
          openChecklist={openChecklist}
          tasks={tasks}
        />
      </FadeCallout>
      <FadeCallout
        className="absolute bottom:0"
        visible={isCollapsed}
        width="collapsed"
      >
        <CollapsedChecklist
          animatedTaskId={animatedTaskId}
          badgeNumber={incompleteTasks.length}
          onClick={openChecklist}
          tasks={tasks}
        />
      </FadeCallout>
      <CSSTransitionGroup
        transitionName={collapsedChecklistTransitions}
        transitionEnterTimeout={250}
        transitionLeaveTimeout={250}
      >
        {showOpen && (
          <OpenChecklist
            animatedTaskId={animatedTaskId}
            currentTaskId={currentTask}
            openTaskId={openTask}
            closeChecklist={closeChecklist}
            openTask={openTaskDisclosure}
            tasks={tasks}
          />
        )}
      </CSSTransitionGroup>
    </div>
  );
}

function initState(checklistStatus) {
  const firstIncomplete = TASK_IDS.find(id => !checklistStatus[id]);

  return () => ({
    currentTask: firstIncomplete,
    openTask: firstIncomplete,
    tasks: [
      {
        id: CREATE_ACCOUNT,
        title: 'Create an account',
        description: '',
        isComplete: true,
        tooltipLabel: 'Create an account',
        completionIcon: '🍰',
        completionMessage: 'Glad you’re here.',
      },
      {
        id: DOWNLOAD_RECORDER,
        title: 'Download Loom',
        cta: <DownloadCTA />,
        isComplete: checklistStatus[DOWNLOAD_RECORDER],
        tooltipLabel: 'Download a recorder to record anywhere, anytime',
        completionIcon: '📹',
        completionMessage: 'You got the thing!',
      },
      {
        id: FIRST_VIDEO_RECORDING,
        title: 'Record a video',
        cta: <RecordCTA />,
        isComplete: checklistStatus[FIRST_VIDEO_RECORDING],
        tooltipLabel:
          'Record your first video to have better and fewer meetings',
        completionIcon: '🎬',
        completionMessage: 'Got it in one.',
      },
      {
        id: SHARE_VIDEO,
        title: 'Share your video',
        cta: <ShareCTA />,
        isComplete: checklistStatus[SHARE_VIDEO],
        tooltipLabel: 'Share your video to get your ideas out',
        completionIcon: '🎁',
        completionMessage: 'They love to see it.',
      },
      {
        id: FIRST_VIDEO_VIEWED,
        title: 'Get a view',
        cta: <ViewCTA />,
        isComplete: checklistStatus[FIRST_VIDEO_VIEWED],
        tooltipLabel: 'Get a view so your teammates understand your ideas',
        completionIcon: '🌈',
        completionMessage: 'You’re Loom-famous.',
      },
      {
        id: ADD_TEAMMATE,
        title: 'Invite a teammate',
        cta: <InviteCTA />,
        isComplete: checklistStatus[ADD_TEAMMATE],
        tooltipLabel: 'Invite a teammate to start collaborating',
        completionIcon: '🎣',
        completionMessage: 'Got ’em!',
      },
    ].sort(sortTasks),
  });
}

function reducer(prevState, action) {
  const state = cloneDeep(prevState);

  switch (action.type) {
    case 'OPEN_DISCLOSURE': {
      state.openTask = action.id;

      return state;
    }
    case 'COMPLETE_TASK': {
      const { task: id } = action;
      let firstIncomplete;

      state.tasks = state.tasks
        .map(task => {
          if (task.id === id) {
            task.isComplete = true;
          }

          if (!task.isComplete && !firstIncomplete) {
            firstIncomplete = task;
          }

          return task;
        })
        .sort(sortTasks);

      state.currentTask = firstIncomplete?.id;

      return state;
    }
    default:
      return prevState;
  }
}

function sortTasks(a, b) {
  if (a.isComplete && !b.isComplete) {
    return -1;
  }

  if (b.isComplete && !a.isComplete) {
    return 1;
  }

  return 0;
}
